import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../../navigation/AuthProvider";
import { Toast } from "../../components/ToastPopup";
import { get_notices, create_notice, update_notice, archive_notice } from "../../utils/noticeRequests";
import "../css/Notices.css";

const END_OPTIONS = [
  { value: "none", label: "Tills vidare" },
  { value: "1", label: "1 dag" },
  { value: "3", label: "3 dagar" },
  { value: "7", label: "1 vecka" },
  { value: "14", label: "2 veckor" },
  { value: "custom", label: "Eget datum…" },
];

const DAY = 86400000;

// ISO -> värde för <input type="datetime-local"> (lokal tid). Utan värde = nu.
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDateTime = (value) =>
  new Date(value).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });

const getStatus = (n) => {
  const now = new Date();
  if (n.archivedAt) return "archived";
  if (n.endsAt && new Date(n.endsAt) < now) return "ended";
  if (n.startsAt && new Date(n.startsAt) > now) return "scheduled";
  return "active";
};

const STATUS_LABEL = {
  active: "Aktivt",
  scheduled: "Kommande",
  ended: "Avslutat",
  archived: "Borttaget",
};

// Senast valda tidsscheman sparas per företag och fylls i nästa gång.
// "Alla" sparas aldrig – det ska alltid väljas aktivt.
const audienceKey = (companyId) => `noticeAudience_${companyId}`;

const loadSavedSchemaIds = (companyId, timeSchemas) => {
  try {
    const raw = localStorage.getItem(audienceKey(companyId));
    if (raw) {
      const saved = JSON.parse(raw).ids || [];
      return saved.filter((id) => timeSchemas.some((t) => t._id === id));
    }
  } catch (e) {}
  return [];
};

const newForm = () => ({
  title: "",
  body: "",
  allSelected: false,
  schemaIds: [],
  important: false,
  pinned: false,
  notify: true,
  startsAt: toLocalInput(null), // "Visas från" = nu som standard
  endChoice: "none",
  endsAtCustom: "",
});

export const Notices = () => {
  const { user, company } = useAuth();

  const [notices, setNotices] = useState([]);
  const [timeSchemas, setTimeSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(newForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [toArchive, setToArchive] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showTime, setShowTime] = useState(false);

  const showToast = (message, type = "success") => setToast({ message, type });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = useCallback(async () => {
    if (!user || !company) return;
    try {
      const data = await get_notices({ user, company, includeInactive: showAll });
      if (data.status === "success") {
        setNotices(data.notices);
        setTimeSchemas(data.timeSchemas || []);
      } else {
        showToast(data.errorText || "Kunde inte hämta meddelanden", "error");
      }
    } catch (e) {
      showToast("Kunde inte hämta meddelanden", "error");
    }
    setLoading(false);
  }, [user, company, showAll]);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(
    () => [...notices].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [notices]
  );

  const schemaName = useMemo(() => {
    const map = {};
    timeSchemas.forEach((t) => (map[t._id] = t.name));
    return map;
  }, [timeSchemas]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...newForm(),
      // Om företaget inte har några tidsscheman finns bara "Alla" att välja
      allSelected: timeSchemas.length === 0,
      schemaIds: loadSavedSchemaIds(company._id, timeSchemas),
    });
    setFormError("");
    setShowTitle(false);
    setShowTime(false);
    setFormOpen(true);
  };

  const openEdit = (n) => {
    setEditing(n);
    setForm({
      title: n.title || "",
      body: n.body || "",
      allSelected: (n.timeSchemas || []).length === 0,
      schemaIds: n.timeSchemas || [],
      important: !!n.important,
      pinned: !!n.pinned,
      notify: false,
      startsAt: toLocalInput(n.startsAt || n.createdAt),
      endChoice: n.endsAt ? "custom" : "none",
      endsAtCustom: n.endsAt ? toLocalInput(n.endsAt) : "",
    });
    setFormError("");
    setShowTitle(!!n.title);
    setShowTime(!!n.endsAt);
    setFormOpen(true);
  };

  const toggleSchema = (id) =>
    setForm((f) => ({
      ...f,
      allSelected: false,
      schemaIds: f.schemaIds.includes(id) ? f.schemaIds.filter((x) => x !== id) : [...f.schemaIds, id],
    }));

  const closeForm = () => {
    if (saving) return;
    if (!editing && (form.title.trim() || form.body.trim()) && !window.confirm("Stäng utan att publicera?")) return;
    setFormOpen(false);
  };

  // Slutdatum som förhandsvisning och som värde att skicka
  const computeEndsAt = () => {
    if (form.endChoice === "none") return null;
    if (form.endChoice === "custom") return form.endsAtCustom ? new Date(form.endsAtCustom) : undefined;
    return new Date(Date.now() + Number(form.endChoice) * DAY);
  };

  // Kontroll + bekräftelse när meddelandet går till hela företaget
  const handleSave = () => {
    if (!form.title.trim() && !form.body.trim()) return setFormError("Skriv ett meddelande.");
    if (!form.allSelected && form.schemaIds.length === 0) return setFormError("Välj vem meddelandet gäller.");
    if (computeEndsAt() === undefined) return setFormError("Välj ett slutdatum.");

    const wasAll = editing ? (editing.timeSchemas || []).length === 0 : false;
    if (form.allSelected && !wasAll) {
      setFormError("");
      setConfirmAll(true);
      return;
    }
    performSave();
  };

  const performSave = async () => {
    setConfirmAll(false);

    const endsAt = computeEndsAt();
    const notice = {
      title: form.title.trim(),
      body: form.body.trim(),
      timeSchemas: form.allSelected ? [] : form.schemaIds,
      important: form.important,
      pinned: form.pinned,
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : new Date().toISOString(),
      endsAt: endsAt ? endsAt.toISOString() : null,
    };

    setSaving(true);
    setFormError("");
    try {
      const data = editing
        ? await update_notice({ user, company, noticeId: editing._id, notice })
        : await create_notice({ user, company, notice, notify: form.notify });

      if (data.status === "success") {
        if (!form.allSelected && form.schemaIds.length > 0) {
          try {
            localStorage.setItem(audienceKey(company._id), JSON.stringify({ ids: form.schemaIds }));
          } catch (e) {}
        }
        setFormOpen(false);
        showToast(editing ? "Meddelandet uppdaterades" : "Meddelandet publicerades");
        await load();
      } else {
        setFormError(data.errorText || "Något gick fel");
      }
    } catch (e) {
      setFormError("Något gick fel");
    }
    setSaving(false);
  };

  const handleArchive = async () => {
    const n = toArchive;
    setToArchive(null);
    try {
      const data = await archive_notice({ user, company, noticeId: n._id });
      if (data.status === "success") {
        showToast("Meddelandet togs bort");
        await load();
      } else {
        showToast(data.errorText || "Kunde inte ta bort", "error");
      }
    } catch (e) {
      showToast("Kunde inte ta bort", "error");
    }
  };

  const renderRecipients = (n) => {
    const ids = n.timeSchemas || [];
    if (ids.length === 0) return <span className="nb-rcpt all">Alla</span>;
    return (
      <div className="nb-rcpt-list">
        {ids.map((id) => (
          <span key={id} className="nb-rcpt">{schemaName[id] || "…"}</span>
        ))}
      </div>
    );
  };

  const endsPreview = computeEndsAt();

  return (
    <div className="nb-page">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="nb-header">
        <div>
          <h1>Anslagstavla</h1>
          <p>Meddelanden till dina anställda – till ett eller flera tidsscheman, eller till alla. De syns i appen och kan skickas som pushnotis.</p>
        </div>
        <button className="nb-btn nb-btn-lg" onClick={openCreate} disabled={loading}>+ Nytt meddelande</button>
      </div>

      <div className="nb-toolbar">
        <div className="nb-segment">
          <button className={!showAll ? "on" : ""} onClick={() => setShowAll(false)}>Aktuella</button>
          <button className={showAll ? "on" : ""} onClick={() => setShowAll(true)}>Alla inkl. avslutade</button>
        </div>
        <span className="nb-count">{sorted.length} {sorted.length === 1 ? "meddelande" : "meddelanden"}</span>
      </div>

      <div className="nb-table-card">
        {loading ? (
          <p className="nb-empty">Laddar…</p>
        ) : sorted.length === 0 ? (
          <div className="nb-empty">
            <strong>Inga meddelanden än</strong>
            <span>Klicka på ”Nytt meddelande” för att skriva till dina anställda.</span>
          </div>
        ) : (
          <table className="nb-table">
            <thead>
              <tr>
                <th>Meddelande</th>
                <th>Mottagare</th>
                <th>Skapad av</th>
                <th>Skapad</th>
                <th>Visas till</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((n) => {
                const status = getStatus(n);
                const archived = status === "archived";
                return (
                  <tr key={n._id} className={status !== "active" ? "dim" : ""}>
                    <td className="nb-col-msg">
                      <div className="nb-msg-tags">
                        {n.important && <span className="nb-tag danger">Viktigt</span>}
                        {n.pinned && <span className="nb-tag info">Fäst på schemasidan</span>}
                      </div>
                      {n.title && <div className="nb-msg-title">{n.title}</div>}
                      {n.body && <div className="nb-msg-body">{n.body}</div>}
                    </td>
                    <td>{renderRecipients(n)}</td>
                    <td>{n.createdByName || "–"}</td>
                    <td className="nowrap">{formatDateTime(n.createdAt)}</td>
                    <td className="nowrap">{n.endsAt ? formatDateTime(n.endsAt) : "Tills vidare"}</td>
                    <td>
                      <span className={`nb-status ${status}`}>{STATUS_LABEL[status]}</span>
                    </td>
                    <td className="nb-actions">
                      {!archived && (
                        <>
                          <button className="nb-btn nb-btn-ghost nb-btn-sm" onClick={() => openEdit(n)}>Redigera</button>
                          <button className="nb-btn nb-btn-danger-ghost nb-btn-sm" onClick={() => setToArchive(n)}>Ta bort</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ---------- Skapa / redigera ---------- */}
      {formOpen && (
        <div className="nb-overlay" onClick={closeForm}>
          <div className="nb-modal nb-modal-simple" onClick={(e) => e.stopPropagation()}>
            <div className="nb-modal-head">
              <h2>{editing ? "Redigera meddelande" : "Nytt meddelande"}</h2>
              <button className="nb-x" onClick={closeForm} aria-label="Stäng">×</button>
            </div>

            <div className="nb-simple-body">
              {/* 1. Meddelandet */}
              {showTitle && (
                <input
                  className="nb-title-input"
                  type="text"
                  maxLength={120}
                  value={form.title}
                  placeholder="Rubrik"
                  onChange={(e) => set({ title: e.target.value })}
                />
              )}
              <textarea
                className="nb-big-text"
                maxLength={4000}
                rows={5}
                value={form.body}
                placeholder="Skriv ditt meddelande…"
                onChange={(e) => set({ body: e.target.value })}
                autoFocus
              />
              {!showTitle && (
                <button type="button" className="nb-link" onClick={() => setShowTitle(true)}>+ Lägg till rubrik</button>
              )}

              {/* 2. Vem */}
              <div className="nb-block">
                <div className="nb-block-label">Vem ska se det?</div>
                <div className="nb-pills">
                  {timeSchemas.map((t) => (
                    <button
                      key={t._id}
                      type="button"
                      className={`nb-pill ${form.schemaIds.includes(t._id) ? "on" : ""}`}
                      onClick={() => toggleSchema(t._id)}
                    >
                      {t.name}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`nb-pill all ${form.allSelected ? "on" : ""}`}
                    onClick={() => set({ allSelected: true, schemaIds: [] })}
                  >
                    Alla (hela företaget)
                  </button>
                </div>
                <p className="nb-hint">
                  {form.allSelected
                    ? "Går till alla anställda i hela företaget."
                    : "Bara de som jobbat i valt tidsschema senaste 3 månaderna, eller är planerade framåt."}
                </p>
              </div>

              {/* 3. Alternativ */}
              <div className="nb-block">
                <div className="nb-block-label">Alternativ</div>
                <div className="nb-chips">
                  <button
                    type="button"
                    className={`nb-chip ${form.important ? "on-danger" : ""}`}
                    onClick={() => set({ important: !form.important })}
                  >
                    {form.important ? "✓ " : ""}Viktigt
                  </button>
                  <button
                    type="button"
                    className={`nb-chip ${form.pinned ? "on" : ""}`}
                    onClick={() => set({ pinned: !form.pinned })}
                  >
                    {form.pinned ? "✓ " : ""}Fäst på schemasidan
                  </button>
                  {!editing && (
                    <button
                      type="button"
                      className={`nb-chip ${form.notify ? "on" : ""}`}
                      onClick={() => set({ notify: !form.notify })}
                    >
                      {form.notify ? "✓ " : ""}Skicka pushnotis
                    </button>
                  )}
                </div>
                <p className="nb-hint">
                  Viktigt visas med röd etikett. Fäst visar meddelandet överst på schemasidan i appen.
                </p>
              </div>

              {/* 4. Tid (dold tills man vill ändra) */}
              <div className="nb-block">
                {!showTime ? (
                  <div className="nb-timeline">
                    <span>Visas från nu, tills vidare.</span>
                    <button type="button" className="nb-link" onClick={() => setShowTime(true)}>Ändra</button>
                  </div>
                ) : (
                  <>
                    <div className="nb-block-label">När ska det visas?</div>
                    <div className="nb-row">
                      <div className="nb-field">
                        <label>Från</label>
                        <input
                          type="datetime-local"
                          value={form.startsAt}
                          onChange={(e) => set({ startsAt: e.target.value })}
                        />
                      </div>
                      <div className="nb-field">
                        <label>Till</label>
                        <select value={form.endChoice} onChange={(e) => set({ endChoice: e.target.value })}>
                          {END_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        {form.endChoice === "custom" && (
                          <input
                            type="datetime-local"
                            className="nb-mt"
                            min={form.startsAt}
                            value={form.endsAtCustom}
                            onChange={(e) => set({ endsAtCustom: e.target.value })}
                          />
                        )}
                      </div>
                    </div>
                    <div className="nb-summary">
                      Visas från {form.startsAt ? formatDateTime(form.startsAt) : "nu"}
                      {endsPreview ? ` till ${formatDateTime(endsPreview)}.` : endsPreview === null ? " och tills vidare." : "."}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="nb-modal-foot">
              {formError ? <p className="nb-error">{formError}</p> : <span />}
              <div className="nb-modal-actions">
                <button className="nb-btn nb-btn-ghost" onClick={closeForm} disabled={saving}>Avbryt</button>
                <button className="nb-btn" onClick={handleSave} disabled={saving}>
                  {saving ? "Sparar…" : editing ? "Spara" : "Publicera"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Bekräfta "Alla" ---------- */}
      {confirmAll && (
        <div className="nb-overlay nb-overlay-top" onClick={() => setConfirmAll(false)}>
          <div className="nb-modal nb-modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="nb-confirm-icon">!</div>
            <h2>Skicka till hela företaget?</h2>
            <p className="nb-confirm-text">
              Alla anställda i hela företaget kommer att se meddelandet
              {!editing && form.notify ? " och få en pushnotis" : ""}. Är du säker?
            </p>
            <div className="nb-modal-actions nb-center">
              <button className="nb-btn nb-btn-ghost" onClick={() => setConfirmAll(false)}>Avbryt</button>
              <button className="nb-btn nb-btn-danger" onClick={performSave}>Ja, skicka till alla</button>
            </div>
          </div>
        </div>
      )}

      {toArchive && (
        <div className="nb-overlay nb-overlay-top" onClick={() => setToArchive(null)}>
          <div className="nb-modal nb-modal-confirm" onClick={(e) => e.stopPropagation()}>
            <h2>Ta bort meddelande?</h2>
            <p className="nb-confirm-text">Det försvinner för alla anställda.</p>
            <div className="nb-modal-actions nb-center">
              <button className="nb-btn nb-btn-ghost" onClick={() => setToArchive(null)}>Avbryt</button>
              <button className="nb-btn nb-btn-danger" onClick={handleArchive}>Ta bort</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notices;
